import { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import jwtDecode from 'jwt-decode';
import config from 'config';

import { Row, Col } from '@guestyci/foundation/Layout';
import Spinner from '@guestyci/foundation/Spinner';
import TextField from '@guestyci/foundation/TextField';
import StatusIndication from '@guestyci/foundation/StatusIndication';

import useAuthorizationModel from 'app/hooks/authorization/useAuthorizationModel';
import navigationsRoute from 'app/routes/navigationsRoute';
import utils from 'utils';
import { useAuthorizationContext } from './AuthorizationContainer';

interface IAutoLoginSuspense {
  children: ReactElement | ReactElement[];
  fallback: ReactElement;
}

function AutoLoginSuspense({ children, fallback }: IAutoLoginSuspense) {
  const {
    autoLogin,
    tokenAuthenticate,
    tokenCurrentUser,
    ssoZendesk,
    isZendeskAuthorization,
  } = useAuthorizationModel();
  const { actions, state } = useAuthorizationContext();
  const [isLoaded, setIsLoaded] = useState(false);
  const [tokenLoginRunning, setTokenLoginRunning] = useState(false);
  const [tokenLoginFailed, setTokenLoginFailed] = useState(false);
  const history = useHistory();
  const { pathname, search } = useLocation();
  const queryParams = useMemo(() => new URLSearchParams(search), []);
  const generatedDeviceUUID = useMemo(() => utils.generateUUID(), []);
  const formRef = useRef<HTMLFormElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const returnTo = queryParams.get('return_to');
  const token = queryParams.get('token');
  const billingRedirectURL = queryParams.get('billingCallbackUrl');
  const isLogoutUrl = history.location.pathname.includes('/logout');
  const { adminLogin } = token
    ? jwtDecode<{ adminLogin: boolean }>(token)
    : { adminLogin: false };

  useEffect(() => {
    async function doTokenAutoLogin() {
      actions.logout();
      utils.clearLocalStorage();
      setTokenLoginRunning(true);
      try {
        const authData = await tokenAuthenticate(token!);
        const userData = await tokenCurrentUser(token!);
        utils.setLocalStorage({
          emailAddress: userData.baseUserInfo.username,
          sessionToken: authData.stoken,
          authenticatedData: userData,
          uuid: generatedDeviceUUID,
          adminLogin,
        });
        utils.setUserSession();
        actions.login();
        setTokenLoginFailed(false);
      } catch (error) {
        console.warn('Token login failure', error);
        setTokenLoginFailed(true);
      }
      setTokenLoginRunning(false);
    }

    if (token) {
      // app can automatically log in using a token in the query string
      doTokenAutoLogin();
    }
  }, [generatedDeviceUUID, token]);

  const isAutologinAllowed =
    state.isAuthorized &&
    !pathname.includes(navigationsRoute.EASY_LOGIN_BASE_URL) &&
    !utils.isAutologinEnabled() &&
    !token &&
    !billingRedirectURL;

  // Session token refresh using the "autologin" endpoint, for the "Remember Me" feature.
  useEffect(() => {
    const { uuid, sessionToken } = utils.getLocalStorageByKey(
      'uuid',
      'sessionToken'
    );

    const zendeskAuth = async () => {
      const jwtToken = await ssoZendesk();
      if (inputRef?.current) {
        inputRef.current.value = jwtToken;
      }
      formRef?.current?.submit();
    };

    const billingAuth = () => {
      const targetURL = new URL(billingRedirectURL || '');
      targetURL.searchParams.set('userId', state.userId);
      window.location.replace(targetURL);
    };

    // Refresh the session token when the user visits the app again.
    const refreshToken = async () => {
      try {
        const response = await autoLogin({
          requestDate: utils.getTimestamp().toString(),
          uuid,
          sessionToken,
        });
        utils.disableAutologinSession();

        utils.setLocalStorage({
          emailAddress: response.baseUserInfo.username,
          sessionToken: response.sessionToken,
          authenticatedData: response,
          uuid,
        });
      } catch (error) {
        console.warn('auto login failure', error);
        actions.logout();
      } finally {
        setIsLoaded(true);
      }
    };

    if (!state.isAuthorized) return setIsLoaded(true);

    if (isAutologinAllowed) {
      refreshToken();
    } else if (!isLogoutUrl) {
      if (isZendeskAuthorization) zendeskAuth();
      if (billingRedirectURL) billingAuth();
    }

    setIsLoaded(true);
  }, [state, isLogoutUrl, isAutologinAllowed, billingRedirectURL]);

  if (tokenLoginRunning) {
    return (
      <>
        <Row fullWidth justify="center">
          <Spinner />
        </Row>
        <Row fullWidth justify="center">
          <TextField>Loading...</TextField>
        </Row>
      </>
    );
  }

  if (tokenLoginFailed) {
    return (
      <Row justify="center">
        <Col align="center" spacing={4} span={4} style={{ padding: '2rem 0' }}>
          <TextField variant="h1">401</TextField>
          <StatusIndication
            variant="redWarning"
            text="This page failed to retrieve your account information."
          />
        </Col>
      </Row>
    );
  }

return (
  <>
    {isZendeskAuthorization && (
      <form
        ref={formRef}
        action={`https://${config.zendeskSubDomain}.zendesk.com/access/jwt${
          returnTo ? '?return_to=' + returnTo : '/'
        }`}
        method="post"
      >
        <input ref={inputRef} type="hidden" name="jwt"></input>
      </form>
    )}
    {isLoaded ? <>{children}</> : fallback}
  </>
);
}

export default AutoLoginSuspense;
